import { createTRPCClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "@passfirst/api/router";

import { API_URL } from "./env";
import { getSessionCookie } from "./auth-client";
import { captureBackendError } from "./observa-capture";

// Retry only when fetch itself throws — i.e. the request never made
// it to the server (DNS failure, transient connection drop, captive
// portal blip mid-quiz). We deliberately do NOT retry on 5xx
// responses: those mean the server saw the request, and a mutation
// like questions.submit could have already created an Attempt row.
// A duplicate submit would silently double-count toward the free
// quota and inflate mastery — bad. Better to surface the error and
// let the user retry knowingly.
//
// Backoff: 200ms, 400ms. Anything longer feels like a hang.
const RETRY_DELAYS_MS = [200, 400];

async function retryingFetch(
  url: RequestInfo | URL,
  options?: RequestInit,
): Promise<Response> {
  // On native the session lives in SecureStore (see auth-client.native.ts),
  // not the cookie store, so attach it explicitly. getSessionCookie() returns
  // "" on web, where credentials:"include" already carries the cookie — so the
  // web request is unchanged.
  const sessionCookie = getSessionCookie();
  const headers = sessionCookie
    ? { ...options?.headers, Cookie: sessionCookie }
    : options?.headers;

  let lastErr: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const res = await fetch(url, { ...options, headers, credentials: "include" });
      // A non-ok Response isn't thrown — tRPC gets it back and fails later
      // with "Unable to transform response from server", logged nowhere.
      // Surface transport faults (404/5xx) to observa here so the app's
      // own backend failures are visible. captureBackendError filters out
      // expected auth 4xx itself.
      if (!res.ok) {
        captureBackendError(String(url), {
          status: res.status,
          statusText: res.statusText,
        });
      }
      return res;
    } catch (err) {
      lastErr = err;
      const delay = RETRY_DELAYS_MS[attempt];
      if (delay === undefined) break;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  // fetch never completed after retries (DNS/connection failure) — the
  // app can't reach the backend at all. Record before rethrowing.
  captureBackendError(String(url), { cause: lastErr });
  throw lastErr;
}

export const trpc = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      fetch: retryingFetch,
    }),
  ],
});

export const API_BASE_URL = API_URL;
